import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import styles from './BrandingSettings.module.scss';
import BrandingPreview, { BrandingOption } from './BrandingPreview/BrandingPreview';
import BrandingForm, { BrandingValues } from './BrandingForm/BrandingForm';
import usePostAccountBranding from '@/hooks/useApi/usePostAccountBranding';
import useFetchAccountBranding from '@/hooks/useApi/useFetchAccountBranding';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useBranding } from '@/providers/Branding.provider';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import BrandingSettingsDialogue from './Dialogue/BrandingSettingsDialogue';
import BrandingSettingsContext, { BrandingSettingsCtx } from './Dialogue/BrandingSettingsContext';
import { BrandingSettingsDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import { PendingDataProvider } from '@/providers/PendingData.provider';
import { UtilButton } from 'UI';
import { useTranslation } from 'react-i18next';

const { Box, Title, Description } = vp;

type FC = FunctionComponent;
const BrandingSettings: FC = () => {
  const [, setPending] = useDataFetchIndicator();
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Settings' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const [resetForm, setResetForm] = useState(false);
  const [flagUpdateThumbnailBranding, fetchThumbnailbranding] = useState(true);
  const { BrandingELinxEnabled, BrandingEmailEnabled, BrandingPortalEnabled } = useLockedFeatures();
  const { fetchAccountBranding } = useFetchAccountBranding();

  const { setBrandingValues } = useBranding();
  const [brandingOptions, setBrandingOptions] = useState<BrandingOption[]>([
    {
      enabled: BrandingEmailEnabled || false,
      productType: vp.BrandingProductType.Email,
    },
    {
      enabled: BrandingELinxEnabled || false,
      productType: vp.BrandingProductType.Elinx,
    },
    {
      enabled: BrandingPortalEnabled || false,
      productType: vp.BrandingProductType.Portal,
    },
  ]);

  useEffect(() => {
    // Fetch account branding for each product type
    async function fetchAccountBrandingForAll() {
      setPending(true);

      const updatedOptions = await Promise.all(
        brandingOptions.map(async (option) => {
          if (option.enabled) {
            const response = await fetchAccountBranding({ Product: option.productType });
            return { ...option, response };
          }
          return option;
        }),
      );

      setPending(false);
      setBrandingOptions(updatedOptions);
    }

    fetchAccountBrandingForAll();
  }, [flagUpdateThumbnailBranding]); // Make sure to adjust the dependencies accordingly
  const [brandingState, setBrandingState] = useState<vp.BrandingProductType>(
    brandingOptions.filter((option) => option.enabled === true)[0]?.productType || vp.BrandingProductType.Email,
  );

  const initalDefault = {
    Logo: '', // vopay logo base 64
    SmallLogo: '', // vopay logo base 64
    PrimaryColor: '#FFD147',
    SecondaryColor: '#2D2D2D',
    ApplyToAll: false,
  };

  const [currentDisplayValues, setCurrentDisplayValues] = useState<BrandingValues>(initalDefault);
  const [defaultValues, setDefaultValues] = useState<BrandingValues>(initalDefault);
  const [, , postAccountBranding] = usePostAccountBranding({});
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [error, setError] = useState('');
  const BrandingSettingsCTX: BrandingSettingsCtx = {
    showSuccessAlert,
    showErrorAlert,
    error,
  };

  const saveBranding = async (values: BrandingValues) => {
    setCurrentDisplayValues(values);

    let hasError = false;
    let errorMessage = '';
    setPending(false);
    setPending(true);

    if (values.ApplyToAll) {
      for (const option of brandingOptions) {
        if (option.enabled) {
          const response = await postAccountBranding({
            Product: option.productType,
            ...values,
          });

          if (response.Success === false) {
            hasError = true;
            errorMessage = response.ErrorMessage;
          }
        }
      }
    } else {
      const response = await postAccountBranding({
        Product: brandingState,
        ...values,
      });

      if (response.Success === false) {
        hasError = true;
        errorMessage = response.ErrorMessage;
      }
    }

    setError(errorMessage);

    if (hasError) {
      setShowErrorAlert(true);
    } else {
      setShowSuccessAlert(true);
    }

    if (values.ApplyToAll || brandingState === vp.BrandingProductType.Portal) {
      const res = await fetchAccountBranding();

      setBrandingValues({
        primaryColor: res.BrandingDetails.PrimaryColor,
        secondaryColor: res.BrandingDetails.SecondaryColor,
        logo: res.Logo,
        smallLogo: res.SmallLogo,
        appTitle: res.AppTitle,
        appFavicon: res.Favicon,
      });
    }

    setPending(false);
    fetchThumbnailbranding(!flagUpdateThumbnailBranding);
  };

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<BrandingSettingsDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: BrandingSettingsDialogueHandler = {
        save: {
          success: {
            default: () => {
              setShowSuccessAlert(false);
            },
          },
          error: {
            default: () => {},
          },
        },
        error: {
          success: {
            default: () => {
              setShowErrorAlert(false);
            },
          },
          error: {
            default: () => {},
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };
  useEffect(() => {
    handleProductChange();
  }, [brandingState]);

  async function handleProductChange() {
    setResetForm(true);

    const res = await fetchAccountBranding({
      Product: brandingState,
    });

    if (!res.Success) return;

    const primaryColor = res.BrandingDetails?.PrimaryColor?.replace('#', '') || initalDefault.PrimaryColor;
    const primaryColorWithHash = primaryColor.startsWith('#') ? primaryColor : `#${primaryColor}`;
    const secondaryColor = res.BrandingDetails?.SecondaryColor?.replace('#', '') || initalDefault.SecondaryColor;
    const secondaryColorWithHash = secondaryColor.startsWith('#') ? secondaryColor : `#${secondaryColor}`;

    setDefaultValues({
      Logo: res.Logo,
      SmallLogo: res.SmallLogo,
      PrimaryColor: primaryColorWithHash,
      SecondaryColor: secondaryColorWithHash,
      ApplyToAll: false,
    });
  }

  useEffect(() => {
    if (resetForm) {
      setResetForm(false);
    }
  }, [resetForm]);

  return (
    <PendingDataProvider>
      <BrandingSettingsContext.Provider value={BrandingSettingsCTX}>
        <div>
          <Box flx="col" mb="md" gapSize="16px">
            <UtilButton
              iconLeft="chevron_left"
              size="large"
              onClick={() => {
                if (history.length > 1) {
                  history.goBack();
                } else {
                  history.replace('/settings');
                }
              }}
            >
              {history.length > 1 ? tGlobal('Buttons.back') : t('pageTitle')}
            </UtilButton>
            <Title title="Branding Settings" />
          </Box>
          <Description>
            Customize how your business appears to your customers when onboarding and receiving payouts.
          </Description>
          <Box pt="xmd">
            <div className={styles.brandingAreaContainer}>
              <div className={styles.brandingForm}>
                <BrandingForm
                  brandingState={brandingState}
                  defaultValues={defaultValues}
                  onChange={setCurrentDisplayValues}
                  onSubmit={saveBranding}
                  reset={resetForm}
                />
              </div>
              <div className={styles.brandingPreview}>
                <BrandingPreview
                  brandingOptions={brandingOptions}
                  brandingValues={{
                    ...currentDisplayValues,
                    PrimaryColor: vp.validateAndReturn(defaultValues.PrimaryColor, currentDisplayValues.PrimaryColor),
                    SecondaryColor: vp.validateAndReturn(
                      defaultValues.SecondaryColor,
                      currentDisplayValues.SecondaryColor,
                    ),
                  }}
                  brandingState={brandingState}
                  onChangeBranding={setBrandingState}
                />
              </div>
            </div>

            <BrandingSettingsDialogue handleDialogue={handlers.handleDialogue} />
          </Box>
        </div>
      </BrandingSettingsContext.Provider>
    </PendingDataProvider>
  );
};

export default BrandingSettings;
